/**
 * Gestor de procesos y validaciones para el sistema de impresión
 */
import { geocodeService } from '../../../services/geocodeService';

class ProcessManager {
    constructor(printModule) {
        this.printModule = printModule;
    }

    /**
     * Obtener configuración de impresión desde el modal
     */
    getPrintConfiguration(modal) {
        try {
            const config = {
                title: modal.querySelector('#print-title')?.value?.trim() || 'Reporte',
                orientation: modal.querySelector('#print-orientation')?.value || 'landscape',
                includeCharts: modal.querySelector('#include-charts')?.checked !== false,
                includeTable: modal.querySelector('#include-table')?.checked !== false,
                notes: modal.querySelector('#print-notes')?.value?.trim() || ''
            };

            console.log('📋 Configuración extraída del modal:', config);
            return config;
        } catch (error) {
            console.error('❌ Error extrayendo configuración:', error);
            return this.getDefaultConfiguration();
        }
    }

    /**
     * Obtener configuración por defecto
     */
    getDefaultConfiguration() {
        const reportType = this.printModule.detectReportType();
        const currentDate = new Date().toLocaleDateString();

        return {
            title: this.printModule.getDefaultTitle(reportType, currentDate),
            orientation: this.printModule.isRecommendedLandscape(reportType) ? 'landscape' : 'portrait',
            includeCharts: true,
            includeTable: true,
            notes: ''
        };
    }

    /**
     * Validar configuración de impresión
     */
    validatePrintConfig(config) {
        console.log('🔍 Validando configuración de impresión...');

        const errors = [];

        // ✅ VALIDAR TÍTULO
        if (!config.title || config.title.length < 3) {
            errors.push('El título debe tener al menos 3 caracteres');
        }

        if (config.title && config.title.length > 100) {
            errors.push('El título no puede tener más de 100 caracteres');
        }

        // ✅ VALIDAR ORIENTACIÓN
        if (!['landscape', 'portrait'].includes(config.orientation)) {
            errors.push('La orientación debe ser horizontal o vertical');
        }

        // ✅ VALIDAR QUE AL MENOS UN CONTENIDO ESTÉ SELECCIONADO
        if (!config.includeCharts && !config.includeTable) {
            errors.push('Debe seleccionar al menos un tipo de contenido (gráficos o tabla)');
        }

        // ✅ VALIDAR NOTAS (OPCIONAL)
        if (config.notes && config.notes.length > 1000) {
            errors.push('Los comentarios no pueden tener más de 1000 caracteres');
        }

        // ✅ MOSTRAR ERRORES SI LOS HAY
        if (errors.length > 0) {
            console.error('❌ Errores de validación:', errors);
            errors.forEach(error => this.printModule.showError(error));
            return false;
        }

        console.log('✅ Configuración válida');
        return true;
    }

    /**
     * Iniciar proceso de impresión completo
     */
    async startPrintProcess(config) {
        console.log('🚀 Iniciando proceso de impresión con configuración:', config);

        try {

            this.currentConfig = config;

            // ✅ FASE 1: PREPARACIÓN
            this.printModule.showLoading('Preparando contenido para impresión...');

            // Esperar un momento para que se cierre el modal
            await this.waitForModalClose();

            // ✅ FASE 2: CAPTURA DE CONTENIDO
            this.printModule.updateLoadingMessage('Capturando gráficos y componentes...');

            const capturedContent = await this.captureReportContent(config);

            // ✅ FASE 3: GENERACIÓN DE PDF
            this.printModule.updateLoadingMessage('Generando documento PDF...');

            const pdfBlob = await this.generateReportPDF(capturedContent, config);

            // ✅ FASE 4: FINALIZACIÓN
            this.printModule.hideLoading();

            // ✅ FASE 5: MOSTRAR PREVIEW
            this.printModule.showPrintPreview(pdfBlob, config);

            console.log('✅ Proceso de impresión completado exitosamente');

        } catch (error) {
            console.error('❌ Error en el proceso de impresión:', error);
            this.printModule.hideLoading();
            this.printModule.showError('Error al generar el reporte: ' + this.getErrorMessage(error));
            throw error;
        }
    }

    /**
     * Esperar a que se cierre el modal
     */
    async waitForModalClose() {
        return new Promise(resolve => setTimeout(resolve, 300));
    }

    /**
     * Capturar contenido del reporte
     */
    async captureReportContent(config) {
        const content = {
            sections: [],
            charts: [],
            map: null,
            table: null,
            nativeTableData: null
        };

        try {
            // 📊 GRÁFICOS - Mantener captura (funciona bien)
            if (config.includeCharts) {
                console.log('📊 Capturando secciones visuales...');

                // ✅ USAR EL MÉTODO DEL MÓDULO PRINCIPAL
                const sections = await this.printModule.captureUniversalSections();

                // ✅ VERIFICAR QUE SEA UN ARRAY
                if (sections && Array.isArray(sections)) {
                    content.sections = sections;
                    console.log(`✅ ${sections.length} secciones capturadas`);
                } else {
                    console.warn('⚠️ captureUniversalSections no devolvió un array válido');
                    content.sections = [];
                }
            }

            // 📋 TABLA - Nuevo enfoque híbrido
            if (config.includeTable) {
                console.log('📋 Extrayendo datos de tabla...');

                // ✅ ESTADÍAS: esperar un poco a que se resuelvan direcciones faltantes
                // (no bloquea indefinidamente; si falla o expira, continúa con fallback actual)
                try {
                    const isEstadias = (this.printModule?.detectReportType?.() === 'estadias') ||
                        (typeof document !== 'undefined' && !!document.querySelector('#tabla-estadias'));
                    if (isEstadias) {
                        await geocodeService.awaitIdle(2500);
                    }
                } catch (e) {
                    // no-op
                }

                if (this.printModule.options.useNativeTables) {
                    // ✅ EXTRAER DATOS Y GENERAR TABLA NATIVA
                    const tableData = await this.printModule.extractTableData();

                    // ✅ VERIFICAR ESTRUCTURA DE DATOS DE TABLA
                    if (tableData && tableData.headers && (tableData.data || tableData.rows)) {
                        content.nativeTableData = {
                            // ✅ PRESERVAR TODAS LAS PROPIEDADES ORIGINALES
                            ...tableData,
                            // ✅ NORMALIZAR data/rows
                            rows: tableData.data || tableData.rows,
                            title: tableData.title || 'Tabla de Datos',
                            totalRows: (tableData.data || tableData.rows).length
                        };
                        console.log(`✅ Datos de tabla extraídos: ${content.nativeTableData.rows.length} filas`);
                    } else {
                        console.warn('⚠️ No se pudieron extraer datos de tabla válidos');
                        content.nativeTableData = null;
                    }
                } else {
                    // 🔄 FALLBACK - Captura tradicional
                    content.table = await this.printModule.captureTable();
                }
            }

            return content;

        } catch (error) {
            console.error('❌ Error capturando contenido:', error);

            // ✅ ASEGURAR QUE SIEMPRE DEVOLVEMOS UN OBJETO VÁLIDO
            return {
                sections: content.sections || [],
                charts: content.charts || [],
                map: content.map || null,
                table: content.table || null,
                nativeTableData: content.nativeTableData || null
            };
        }
    }

    /**
     * Capturar secciones visuales (gráficos, mapas, etc.)
     */
    async captureVisualSections() {
        try {
            // ✅ VERIFICAR QUE EL MÉTODO EXISTE EN EL MÓDULO PRINCIPAL
            if (typeof this.printModule.captureUniversalSections === 'function') {
                console.log('📊 Capturando secciones con captureUniversalSections...');
                return await this.printModule.captureUniversalSections();
            } else if (typeof this.printModule.captureBootstrapSections === 'function') {
                console.log('📊 Capturando secciones con captureBootstrapSections...');
                return await this.printModule.captureBootstrapSections();
            } else {
                console.error('❌ No se encontró método de captura de secciones');
                return [];
            }
        } catch (error) {
            console.error('❌ Error capturando secciones visuales:', error);
            return [];
        }
    }

    /**
     * Extraer datos de tabla para tabla nativa
     */
    async extractTableData() {
        try {
            // ✅ VERIFICAR QUE EL MÉTODO EXISTE
            if (typeof this.printModule.extractTableData === 'function') {
                console.log('📋 Extrayendo datos de tabla...');
                return await this.printModule.extractTableData();
            } else if (typeof this.printModule.extractUniversalTableData === 'function') {
                console.log('📋 Extrayendo datos universales de tabla...');
                return await this.printModule.extractUniversalTableData();
            } else {
                console.error('❌ No se encontró método de extracción de tabla');
                return null;
            }
        } catch (error) {
            console.error('❌ Error extrayendo datos de tabla:', error);
            return null;
        }
    }

    /**
     * Capturar imagen de tabla (fallback)
     */
    async captureTableImage() {
        try {
            // ✅ VERIFICAR QUE EL MÉTODO EXISTE
            if (typeof this.printModule.captureTable === 'function') {
                console.log('📷 Capturando imagen de tabla...');
                return await this.printModule.captureTable();
            } else {
                console.error('❌ No se encontró método captureTable');
                return null;
            }
        } catch (error) {
            console.error('❌ Error capturando imagen de tabla:', error);
            return null;
        }
    }

    /**
     * Generar PDF del reporte
     */
    async generateReportPDF(content, config) {
        console.log('📄 Generando PDF con configuración:', config.orientation);

        try {
            // ✅ VERIFICAR QUE EL MÉTODO EXISTE
            if (typeof this.printModule.generateReportPDF === 'function') {
                console.log('📄 Generando PDF con método del módulo principal...');
                return await this.printModule.generateReportPDF(content, config);
            } else {
                throw new Error('Método generateReportPDF no encontrado en PrintReportModule');
            }
        } catch (error) {
            console.error('❌ Error generando PDF:', error);
            throw new Error('Error al generar el documento PDF: ' + error.message);
        }
    }

    /**
     * Actualizar mensaje de loading
     */
    updateLoadingMessage(message) {
        try {
            // ✅ LLAMAR DIRECTAMENTE AL UIMANAGER, NO AL PRINTMODULE
            if (this.printModule.uiManager && typeof this.printModule.uiManager.updateLoadingMessage === 'function') {
                this.printModule.uiManager.updateLoadingMessage(message);
            } else {
                // ✅ FALLBACK DIRECTO SIN RECURSIÓN
                const loading = document.getElementById('print-loading');
                if (loading) {
                    const messageEl = loading.querySelector('div[style*="font-size: 18px"]');
                    if (messageEl) {
                        messageEl.textContent = message;
                    }
                }
            }
        } catch (error) {
            console.warn('⚠️ No se pudo actualizar mensaje de loading:', error);
        }
    }

    /**
     * Obtener mensaje de error amigable
     */
    getErrorMessage(error) {
        const errorMessages = {
            'NetworkError': 'Error de conexión. Verifique su conexión a internet.',
            'SecurityError': 'Error de seguridad. No se pueden capturar algunos elementos.',
            'QuotaExceededError': 'Memoria insuficiente. Intente con un reporte más pequeño.',
            'TimeoutError': 'El proceso tardó demasiado. Intente nuevamente.',
            'TypeError': 'Error interno del sistema. Contacte al administrador.',
            'ReferenceError': 'Error de configuración. Recargue la página e intente nuevamente.'
        };

        // Buscar por tipo de error
        for (const [errorType, message] of Object.entries(errorMessages)) {
            if (error.name === errorType || error.message.includes(errorType)) {
                return message;
            }
        }

        // Buscar por palabras clave en el mensaje
        const message = error.message.toLowerCase();

        if (message.includes('network') || message.includes('fetch')) {
            return 'Error de conexión. Verifique su conexión a internet.';
        }

        if (message.includes('permission') || message.includes('blocked')) {
            return 'Permisos insuficientes. Verifique la configuración del navegador.';
        }

        if (message.includes('memory') || message.includes('quota')) {
            return 'Memoria insuficiente. Cierre otras pestañas e intente nuevamente.';
        }

        if (message.includes('timeout') || message.includes('tiempo')) {
            return 'El proceso tardó demasiado. Intente con un rango de fechas menor.';
        }

        if (message.includes('canvas') || message.includes('render')) {
            return 'Error renderizando contenido. Actualice la página e intente nuevamente.';
        }

        // Error genérico con información útil
        return error.message || 'Error desconocido en el proceso de impresión';
    }

    /**
     * Validar entorno del navegador
     */
    validateBrowserEnvironment() {
        const issues = [];
        const warnings = [];

        // ✅ CAMBIAR A WARNINGS EN LUGAR DE ERRORES BLOQUEANTES
        if (typeof html2canvas === 'undefined') {
            warnings.push('Librería html2canvas no disponible - se cargará automáticamente');
            console.warn('⚠️ html2canvas no encontrado globalmente');
        }

        if (typeof jsPDF === 'undefined') {
            warnings.push('Librería jsPDF no disponible - se cargará automáticamente');
            console.warn('⚠️ jsPDF no encontrado globalmente');
        }

        // ✅ SOLO CONSIDERAR ERRORES CRÍTICOS
        if (!window.URL || !window.URL.createObjectURL) {
            issues.push('API URL no disponible');
        }

        if (!document.createElement('canvas').getContext) {
            issues.push('Canvas no soportado');
        }

        // ✅ LOG DE ESTADO
        if (warnings.length > 0) {
            console.warn('⚠️ Advertencias del entorno:', warnings);
        }

        // ✅ SOLO FALLAR SI HAY PROBLEMAS CRÍTICOS REALES
        if (issues.length > 0) {
            console.error('❌ Problemas críticos:', issues);
            return { isValid: false, issues, warnings };
        }

        console.log('✅ Entorno validado - continuando con advertencias si las hay');
        return { isValid: true, issues: [], warnings };
    }

    /**
     * Optimizar configuración según el entorno
     */
    optimizeConfigForEnvironment(config) {
        const optimizedConfig = { ...config };

        // ✅ OPTIMIZACIONES SEGÚN MEMORIA
        if (navigator.deviceMemory && navigator.deviceMemory < 4) {
            console.log('📱 Dispositivo con memoria limitada - aplicando optimizaciones');

            // Reducir calidad de captura
            if (this.printModule.options.captureQuality > 0.8) {
                this.printModule.options.captureQuality = 0.8;
            }

            // Preferir tablas nativas sobre imágenes
            this.printModule.options.useNativeTables = true;
        }

        // ✅ OPTIMIZACIONES SEGÚN CONEXIÓN
        if (navigator.connection) {
            const connection = navigator.connection;

            if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
                console.log('🐌 Conexión lenta detectada - aplicando optimizaciones');

                // Reducir calidad de captura
                this.printModule.options.captureQuality = 0.7;

                // Preferir orientación vertical (archivos más pequeños)
                if (optimizedConfig.orientation === 'landscape') {
                    console.log('📱 Cambiando a orientación vertical por conexión lenta');
                    optimizedConfig.orientation = 'portrait';
                }
            }
        }

        return optimizedConfig;
    }

    /**
     * Estimar tiempo de procesamiento
     */
    estimateProcessingTime(config) {
        let estimatedTime = 2000; // Base: 2 segundos

        // ✅ TIEMPO POR SECCIONES VISUALES
        if (config.includeCharts) {
            // Estimar basado en elementos DOM
            const visualElements = document.querySelectorAll('.row.g-3, .chart-container, canvas').length;
            estimatedTime += visualElements * 1500; // 1.5 segundos por elemento
        }

        // ✅ TIEMPO POR TABLA
        if (config.includeTable) {
            if (this.printModule.options.useNativeTables) {
                // Tabla nativa es más rápida
                const potentialRows = document.querySelectorAll('tr, .alert-row').length;
                estimatedTime += Math.min(potentialRows * 10, 5000); // Max 5 segundos para tabla
            } else {
                // Captura de imagen de tabla es más lenta
                estimatedTime += 3000;
            }
        }

        // ✅ AJUSTAR SEGÚN ORIENTACIÓN
        if (config.orientation === 'landscape') {
            estimatedTime *= 1.2; // 20% más tiempo para horizontal
        }

        // ✅ AJUSTAR SEGÚN DISPOSITIVO
        if (navigator.deviceMemory && navigator.deviceMemory < 4) {
            estimatedTime *= 1.5; // 50% más tiempo en dispositivos lentos
        }

        return Math.round(estimatedTime);
    }

    /**
     * Preparar sistema para proceso intensivo
     */
    async prepareForIntensiveProcess() {
        console.log('⚙️ Preparando sistema para proceso intensivo...');

        // ✅ LIMPIEZA DE MEMORIA
        if (window.gc && typeof window.gc === 'function') {
            try {
                window.gc(); // Garbage collection si está disponible
            } catch (e) {
                // Ignorar si no está disponible
            }
        }

        // ✅ PAUSAR ANIMACIONES Y TRANSICIONES
        const style = document.createElement('style');
        style.id = 'print-performance-optimizations';
        style.textContent = `
            *, *::before, *::after {
                animation-duration: 0s !important;
                animation-delay: 0s !important;
                transition-duration: 0s !important;
                transition-delay: 0s !important;
            }
        `;
        document.head.appendChild(style);

        // ✅ ESPERAR FRAME SIGUIENTE
        return new Promise(resolve => requestAnimationFrame(resolve));
    }

    /**
     * Restaurar sistema después del proceso
     */
    async restoreAfterIntensiveProcess() {
        console.log('🔄 Restaurando sistema después del proceso...');

        // ✅ REMOVER OPTIMIZACIONES DE RENDIMIENTO
        const optimizationStyle = document.getElementById('print-performance-optimizations');
        if (optimizationStyle) {
            optimizationStyle.remove();
        }

        // ✅ ESPERAR FRAME SIGUIENTE
        return new Promise(resolve => requestAnimationFrame(resolve));
    }

    /**
     * Debug del proceso
     */
    debugProcess(config, content, timings = {}) {
        console.log('🔍 DEBUG DEL PROCESO DE IMPRESIÓN:');
        console.log('📋 Configuración:', config);
        console.log('📊 Contenido capturado:', {
            sections: content.sections?.length || 0,
            nativeTable: content.nativeTableData?.rows?.length || 0,
            imageTable: !!content.table
        });

        if (timings.start && timings.end) {
            console.log(`⏱️ Tiempo total: ${timings.end - timings.start}ms`);
        }

        // ✅ INFORMACIÓN DEL NAVEGADOR
        console.log('🌐 Entorno:', {
            userAgent: navigator.userAgent.substring(0, 50) + '...',
            memory: navigator.deviceMemory || 'N/A',
            connection: navigator.connection?.effectiveType || 'N/A',
            online: navigator.onLine
        });

        // ✅ ESTADÍSTICAS DE LA PÁGINA
        console.log('📄 Estadísticas DOM:', {
            totalElements: document.querySelectorAll('*').length,
            canvasElements: document.querySelectorAll('canvas').length,
            tableElements: document.querySelectorAll('table, [role="table"]').length,
            rowElements: document.querySelectorAll('tr, .alert-row').length
        });
    }
}

export default ProcessManager;